/**
 * Signup API client — talks to the Node signup-service (docs/signup.md §2/§3),
 * NEVER directly to PocketBase for org/user creation.
 *
 * Base URL resolution: same-origin by default. In dev (Vite on 5173) the
 * service is reached via the Vite proxy (vite.config.js server.proxy), so
 * relative /api/signup/* works in both dev and production.
 */

const BASE =
  import.meta.env?.VITE_SIGNUP_API_URL || import.meta.env?.VITE_POCKETBASE_URL || '';

function url(path) {
  return `${BASE.replace(/\/$/, '')}${path}`;
}

/**
 * Create a Stripe Checkout session for a new organization signup.
 * @returns {Promise<{url: string, sessionId: string}>}
 */
export async function createSignupCheckout({
  companyName,
  contactName,
  email,
  phone,
  plan,
  trades
}) {
  const res = await fetch(url('/api/signup/checkout'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyName, contactName, email, phone, plan, trades })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = new Error(data?.error || `Signup service error (${res.status})`);
    e.status = res.status;
    throw e;
  }
  return data;
}

/**
 * Poll post-checkout account provisioning status.
 * @returns {Promise<{status: 'pending'|'ready'|'payment_failed', organization?: object}>}
 */
export async function getSignupStatus(email, sessionId) {
  const qs = new URLSearchParams({ email });
  if (sessionId) qs.set('session_id', sessionId);
  const res = await fetch(url(`/api/signup/status?${qs}`), {
    cache: 'no-store'
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || `Signup service error (${res.status})`);
  }
  return data;
}
